// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { getWebhookDeliveries, retryFailedWebhooks, testWebhook, getBanks } from "../lib/api";
import { RefreshCw, Send, Clock, CheckCircle, XCircle, AlertCircle, Webhook } from "lucide-react";

const Webhooks = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [filters, setFilters] = useState({
    bank_code: "",
    status: "",
    limit: 50
  });

  const fetchDeliveries = useCallback(async () => {
    try {
      const params = {};
      if (filters.bank_code) params.bank_code = filters.bank_code;
      if (filters.status) params.delivery_status = filters.status;
      params.limit = filters.limit;

      const response = await getWebhookDeliveries(params);
      setDeliveries(response.data);
    } catch (error) {
      toast.error("Failed to fetch webhook deliveries");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchBanks = useCallback(async () => {
    try {
      const response = await getBanks();
      setBanks(response.data);
    } catch (error) {
      console.error("Failed to fetch banks");
    }
  }, []);

  useEffect(() => {
    fetchBanks();
    fetchDeliveries();
  }, [fetchBanks, fetchDeliveries]);

  const handleRetryFailed = async () => {
    setRetrying(true);
    try {
      const response = await retryFailedWebhooks(24);
      toast.success(`Retried ${response.data.retried_count} failed webhooks`);
      fetchDeliveries();
    } catch (error) {
      toast.error("Failed to retry webhooks");
    } finally {
      setRetrying(false);
    }
  };

  const handleTestWebhook = async (bankCode) => {
    try {
      await testWebhook(bankCode);
      toast.success(`Test webhook sent to ${bankCode}`);
      setTimeout(fetchDeliveries, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test webhook");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "delivered":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30"><CheckCircle className="h-3 w-3 mr-1" /> Delivered</Badge>;
      case "failed":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="h-3 w-3 mr-1" /> Failed</Badge>;
      case "pending":
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30"><AlertCircle className="h-3 w-3 mr-1" /> {status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  // Calculate stats
  const stats = {
    total: deliveries.length,
    delivered: deliveries.filter(d => d.status === "delivered").length,
    failed: deliveries.filter(d => d.status === "failed").length,
    pending: deliveries.filter(d => d.status === "pending").length
  };

  return (
    <div className="space-y-6" data-testid="webhooks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Webhook className="h-6 w-6 text-primary" />
            Webhook Deliveries
          </h1>
          <p className="text-muted-foreground">Monitor webhook notifications sent to banks</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchDeliveries}
            disabled={loading}
            data-testid="refresh-webhooks-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleRetryFailed}
            disabled={retrying}
            data-testid="retry-webhooks-btn"
          >
            <Send className={`h-4 w-4 mr-2 ${retrying ? 'animate-pulse' : ''}`} />
            Retry Failed
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card/50 backdrop-blur">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-sm text-muted-foreground">Total Deliveries</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 backdrop-blur border-emerald-500/30">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-emerald-400">{stats.delivered}</div>
            <p className="text-sm text-muted-foreground">Delivered</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 backdrop-blur border-red-500/30">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-red-400">{stats.failed}</div>
            <p className="text-sm text-muted-foreground">Failed</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 backdrop-blur border-amber-500/30">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-400">{stats.pending}</div>
            <p className="text-sm text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Bank</label>
              <Select
                value={filters.bank_code}
                onValueChange={(value) => setFilters(prev => ({ ...prev, bank_code: value === "all" ? "" : value }))}
              >
                <SelectTrigger data-testid="filter-bank">
                  <SelectValue placeholder="All Banks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Banks</SelectItem>
                  {banks.map(bank => (
                    <SelectItem key={bank.bank_code} value={bank.bank_code}>{bank.bank_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Status</label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value === "all" ? "" : value }))}
              >
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Limit</label>
              <Input
                type="number"
                value={filters.limit}
                onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value) || 50 }))}
                min={10}
                max={500}
                data-testid="filter-limit"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={fetchDeliveries} className="w-full" data-testid="apply-filters-btn">
                Apply Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Webhooks */}
      <Card className="bg-card/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg">Test Webhooks</CardTitle>
          <CardDescription>Send a test webhook to a bank&apos;s configured endpoint</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {banks.map(bank => (
              <Button
                key={bank.bank_code}
                variant="outline"
                size="sm"
                onClick={() => handleTestWebhook(bank.bank_code)}
                data-testid={`test-webhook-${bank.bank_code}`}
              >
                <Send className="h-3 w-3 mr-1" />
                {bank.bank_code}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Deliveries Table */}
      <Card className="bg-card/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg">Delivery History</CardTitle>
          <CardDescription>Recent webhook delivery attempts</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading deliveries...</div>
          ) : deliveries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No webhook deliveries found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attempts</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last Attempt</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deliveries.map((delivery, index) => (
                    <TableRow key={delivery.id || index} data-testid={`delivery-row-${index}`}>
                      <TableCell className="font-mono text-xs">
                        {delivery.event_type || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{delivery.bank_code || "Central"}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(delivery.status)}</TableCell>
                      <TableCell>{delivery.attempt_count || 0} / 3</TableCell>
                      <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">
                        {delivery.url || "-"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(delivery.created_at)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(delivery.last_attempt_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Webhooks;
